import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import useAppStore from '../../store';
import DataModeBadge from '../../components/feedback/DataModeBadge.jsx';

const STATUS_TONES = {
  healthy: 'var(--status-running)',
  failed: 'var(--status-failed)'
};

/**
 * Local fallback schedule — mocks/dashboard.js intentionally has no
 * cron/nextRun fields, so the strip ships a deterministic preview
 * (offsets in ms from now) until realtime cron events exist.
 */
const LOCAL_PREVIEW_RUNS = [
  { id: 'cron-atlas', agent: 'Atlas', label: 'orchestration sweep', offsetMs: 4 * 60 * 1000 },
  { id: 'cron-scout', agent: 'Scout', label: 'market intelligence scan', offsetMs: 11 * 60 * 1000 },
  { id: 'cron-ledger', agent: 'Ledger', label: 'invoice reconciliation', offsetMs: 26 * 60 * 1000 },
  { id: 'cron-forge', agent: 'Forge', label: 'nightly build triage', offsetMs: 47 * 60 * 1000 },
  { id: 'cron-vector', agent: 'Vector', label: 'growth report digest', offsetMs: 73 * 60 * 1000 }
];

function resolveDate(value) {
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'string') return new Date(value);
  return null;
}

function formatNextRun(event) {
  const date = resolveDate(event?.nextRunAt ?? event?.timestamp);
  if (date && !Number.isNaN(date.getTime())) {
    return formatDistanceToNow(date, { addSuffix: true });
  }
  return typeof event?.nextRunIn === 'string' ? event.nextRunIn : 'queued';
}

/**
 * Bottom strip (T2a): "Next autonomous runs" — the upcoming scheduled
 * triggers per agent. Events come from the realtime slice when present
 * (cron events carry nextRunAt); otherwise the local preview above is
 * shown (mocks/dashboard.js has no cron fields by design).
 *
 * Cutover §3 honesty: the old "scheduler healthy" stamp asserted a live
 * scheduler status the platform does not measure. The strip now labels its
 * source (schedule preview from the sample seed — a DataModeBadge) and the
 * status chip only claims "healthy" when REAL cron events are streaming.
 */
function CronPreviewStrip() {
  const realtimeEvents = useAppStore((state) => state.realtimeEvents);

  const runs = (Array.isArray(realtimeEvents) ? realtimeEvents : [])
    .filter((event) => event?.type === 'cron' && (event.nextRunAt || event.nextRunIn))
    .slice(0, 6);

  const isLive = runs.length > 0;
  const preview = isLive
    ? runs
    : LOCAL_PREVIEW_RUNS.map((run) => ({ ...run, nextRunAt: new Date(Date.now() + run.offsetMs) }));

  return (
    <section className="cron-strip panel">
      <div className="section-heading">
        <div>
          <h2>
            Next autonomous runs{' '}
            <DataModeBadge
              mode={isLive ? 'live' : 'sample'}
              label={isLive ? null : 'Schedule preview — not a live scheduler feed'}
            />
          </h2>
          <span>Scheduled triggers</span>
        </div>
        <span className="cron-strip-status" style={{ color: STATUS_TONES.healthy }}>
          <FiIcons.FiCheckCircle size={13} /> {isLive ? 'scheduler healthy' : 'preview schedule'}
        </span>
      </div>
      <div className="cron-strip-list">
        {preview.map((run, index) => (
          <div className="cron-strip-item" key={run.id ?? `${run.agent}-${index}`}>
            <FiIcons.FiClock size={13} />
            <strong>{run.agent}</strong>
            <span>{run.label}</span>
            <em>{formatNextRun(run)}</em>
          </div>
        ))}
      </div>
    </section>
  );
}

export default CronPreviewStrip;
